import React, { useState } from 'react';
import { XCircle, Loader2, AlertCircle, FileText, Stethoscope, ExternalLink, Trash2 } from 'lucide-react';
import { UserRequest, RequestStatus } from '../types';

interface RequestCardProps {
  request: UserRequest;
  isSelected: boolean;
  onClick: () => void;
  onPatientClick?: (patientId: string, request: UserRequest) => void;
  onDelete?: (requestId: string) => void;
}

export const RequestCard: React.FC<RequestCardProps> = ({
  request,
  isSelected,
  onClick,
  onPatientClick,
  onDelete
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // Get status icon and color
  const getStatusIcon = (status: RequestStatus) => {
    const statusStr = String(status).toLowerCase();
    const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
    const isOld = new Date(request.createdAt) < thirtyMinutesAgo;
    
    // Check if pending, inprogress, or processing request is older than 30 minutes
    if ((statusStr === 'pending' || statusStr === 'inprogress' || statusStr === 'processing') && isOld) {
      return <XCircle size={16} className="text-error" />;
    }
    
    switch (statusStr) {
      case 'completed':
        return null; // No icon for completed requests
      case 'failed':
        return <XCircle size={16} className="text-error" />;
      case 'inprogress':
      case 'processing':
        return <Loader2 size={16} className="text-primary animate-spin" />;
      case 'pending':
        return <Loader2 size={16} className="text-warning animate-spin" />;
      default:
        return <AlertCircle size={16} className="text-base-content/50" />;
    }
  };

  // Get task type icon
  const getTaskTypeIcon = (taskName: string) => {
    const taskNameLower = (taskName || '').toLowerCase();
    if (taskNameLower.includes('note') || taskNameLower.includes('soap')) {
      return <Stethoscope size={16} className="text-primary-content" />;
    }
    return <FileText size={16} className="text-base-content/70" />;
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Get status text
  const getStatusText = (status: RequestStatus) => {
    const statusStr = String(status).toLowerCase();
    const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
    const isOld = new Date(request.createdAt) < thirtyMinutesAgo;
    
    // Check if pending, inprogress, or processing request is older than 30 minutes
    if ((statusStr === 'pending' || statusStr === 'inprogress' || statusStr === 'processing') && isOld) {
      return 'Failed';
    }
    
    switch (statusStr) {
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'inprogress':
      case 'processing':
        return 'Processing...';
      case 'pending':
        return 'Pending';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  };

  // Check if request is processing (exclude old requests that should be treated as failed)
  const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
  const isOld = new Date(request.createdAt) < thirtyMinutesAgo;
  const statusStr = String(request.status).toLowerCase();
  const isProcessing = (statusStr === 'inprogress' || statusStr === 'processing' || statusStr === 'pending') && !isOld;

  const patientName = request.patientName || 'No Patient Selected';
  
  // Check if patient is actually selected (has a valid patientId AND patientName is not the default)
  const hasPatient = request.patientId && 
                     request.patientId.trim() !== '' && 
                     patientName !== 'No Patient Selected' &&
                     request.patientName && 
                     request.patientName.trim() !== '';

  const handlePatientNameClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (onPatientClick && hasPatient) {
      onPatientClick(request.patientId, request);
    }
  };

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (onDelete) {
      await onDelete(request.id);
    }
    setShowDeleteModal(false);
  };

  const handleCancelDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setShowDeleteModal(false);
  };

  return (
    <div
      className={`
        relative p-3 mb-2 rounded-lg transition-all duration-200 border
        ${isProcessing 
          ? 'cursor-not-allowed opacity-75' 
          : 'cursor-pointer hover:bg-base-200 hover:border-base-400'
        }
        ${isSelected 
          ? 'bg-primary/10 border-primary' 
          : 'bg-base-100 border-base-300'
        }
      `}
      onClick={isProcessing ? undefined : onClick}
    >
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2 flex-1">
          {getTaskTypeIcon(request.taskName)}
          {hasPatient && onPatientClick ? (
            <button
              type="button"
              onClick={handlePatientNameClick}
              onMouseDown={(e) => e.stopPropagation()}
              className="font-semibold text-sm text-base-content underline hover:text-base-content/70 hover:no-underline text-left cursor-pointer bg-transparent border-none p-0 relative z-50 transition-all flex items-center gap-1"
              style={{ pointerEvents: 'auto' }}
            >
              {patientName}
              <ExternalLink size={12} className="text-base-content/60" />
            </button>
          ) : (
            <span className="font-semibold text-sm text-base-content">
              {patientName}
            </span>
          )}
        </div>
        <div className="flex items-center">
          {getStatusIcon(request.status)}
        </div>
      </div>
      
      <div className="flex flex-col gap-1">
        <span className="text-xs text-base-content/60">
          {formatDate(request.createdAt)}
        </span>
        <span className="text-sm font-medium text-base-content/80">
          {request.taskName || 'Unknown Task'}
        </span>
        {String(request.status).toLowerCase() !== 'completed' && (
          <span className="text-xs font-medium text-base-content/60">
            {getStatusText(request.status)}
          </span>
        )}
      </div>

      {/* Progress indicator for active requests */}
      {(String(request.status).toLowerCase() === 'inprogress' || String(request.status).toLowerCase() === 'processing' || String(request.status).toLowerCase() === 'pending') && (
        (() => {
          // Don't show progress for old pending, inprogress, or processing requests
          const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000);
          const isOld = new Date(request.createdAt) < thirtyMinutesAgo;
          if (isOld) {
            return null;
          }
          return (
            <div className="mt-3 pr-12">
              <div className="w-full h-1 bg-base-300 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-primary to-primary-focus rounded-full animate-pulse"></div>
              </div>
            </div>
          );
        })()
      )}

      {/* Delete button in bottom right corner */}
      {onDelete && (
        <button
          type="button"
          onClick={handleDeleteClick}
          onMouseDown={(e) => e.stopPropagation()}
          className="absolute bottom-2 right-2 btn btn-ghost btn-xs text-error/40 hover:text-error hover:bg-error/10 transition-colors"
          style={{ pointerEvents: 'auto' }}
          title="Delete request"
        >
          <Trash2 size={14} />
        </button>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <dialog open className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">Delete Request</h3>
            <p className="py-4">
              Are you sure you want to delete this request? This action cannot be undone.
            </p>
            <div className="modal-action">
              <button
                className="btn btn-ghost"
                onClick={handleCancelDelete}
              >
                Cancel
              </button>
              <button
                className="btn btn-error text-white"
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop" onClick={handleCancelDelete}>
            <button>close</button>
          </form>
        </dialog>
      )}
    </div>
  );
}; 